"use client";

import { Button, type ButtonProps } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Icon } from "@repo/dashboard-core";
import { useTranslations } from "next-intl";
import { useRef, useState, type ReactNode } from "react";

import { fetchFiles, getFileUploadUrl } from "@/lib/actions/files";

interface UploadCtaProps extends Omit<ButtonProps, "onClick" | "children"> {
  /**
   * Callback fired after a successful upload with the final stored file name
   * (the api prefixes uploads, so this differs from the picked file's name).
   * Parents can refresh a list or navigate to the editor with it.
   */
  onUploaded?: (storedFileName: string) => void | Promise<void>;
  children?: ReactNode;
}

/**
 * Upload flow shared by the My Files CTA button and the shell quick action:
 * picks a file, requests a presigned URL, uploads via PUT, notifies.
 * Mount the returned inputRef/handleChange on a hidden file input and call
 * openPicker to start the flow.
 */
export function useFileUpload(
  onUploaded?: (storedFileName: string) => void | Promise<void>,
) {
  const t = useTranslations("my-files");
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  async function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-selecting the same file
    if (!file) return;

    setUploading(true);
    try {
      const urlResult = await getFileUploadUrl(file.name);
      if (!urlResult.success) throw new Error(urlResult.message);

      const uploadResponse = await fetch(urlResult.data.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/octet-stream" },
      });
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed (${uploadResponse.status})`);
      }

      notifications.show({
        title: t("success.upload-title"),
        message: t("success.upload-description"),
        color: "green",
      });

      // Ensure server state is fresh before we let the parent refetch.
      await fetchFiles().catch(() => null);
      await onUploaded?.(urlResult.data.fileName);
    } catch (err) {
      notifications.show({
        title: t("error"),
        message: err instanceof Error ? err.message : t("upload-failed"),
        color: "red",
      });
    } finally {
      setUploading(false);
    }
  }

  return {
    inputRef,
    uploading,
    handleChange,
    openPicker: () => inputRef.current?.click(),
  };
}

/**
 * Upload button: picks a file, requests a presigned URL, uploads via PUT.
 *
 * Shared between the My Files header and (later) the sidebar / FAB.
 */
export function UploadCta({
  onUploaded,
  children,
  ...buttonProps
}: UploadCtaProps) {
  const t = useTranslations("my-files");
  const { inputRef, uploading, handleChange, openPicker } =
    useFileUpload(onUploaded);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={handleChange}
        aria-hidden
        tabIndex={-1}
      />
      <Button
        loading={uploading}
        onClick={openPicker}
        leftSection={<Icon name="plus" size={18} />}
        {...buttonProps}
      >
        {children ?? t("upload-file")}
      </Button>
    </>
  );
}
